import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import type { Project } from "@theirstory/common/api_types/backend";

import type { RootState } from "../../../store/state/rootReducer";
import type { AppDispatch } from "../../../store/state/rootStore";
import { getAllProjects } from "../../../store/actions/projects";

interface MoveToProjectModalProps {
  showModal: boolean;
  onCancel: () => void;
  onConfirm: (targetProjectId: string) => Promise<void>;
}

export const MoveToProjectModal = ({ showModal, onCancel, onConfirm }: MoveToProjectModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { project_id: currentProjectId } = useParams();
  const userProjects = useSelector((state: RootState) => state.projects.userProjects);
  const isLoadingProjects = useSelector((state: RootState) => state.projects.isLoadingProjects);
  const selectedStories = useSelector((state: RootState) => state.stories.selectedStories);
  const [targetProjectId, setTargetProjectId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableProjects = userProjects?.filter((project: Project) => project._id !== currentProjectId);

  useEffect(() => {
    if (!showModal || userProjects.length || isLoadingProjects) {
      return;
    }

    dispatch(getAllProjects()).catch((error) => {
      console.error("Failed to load projects for move modal", error);
    });
  }, [dispatch, isLoadingProjects, showModal, userProjects.length]);

  useEffect(() => {
    if (!showModal) {
      setTargetProjectId("");
      setIsSubmitting(false);
    }
  }, [showModal]);

  const handleConfirm = async () => {
    if (!targetProjectId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(targetProjectId);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={showModal}
      onClose={onCancel}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          background: "white",
          width: "min(560px, 92vw)",
          padding: "24px",
          borderRadius: "5px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography fontSize="20px" fontWeight={700} color="rgba(0, 0, 0, 0.87)">
          Move to project
        </Typography>
        <Box>
          <Typography fontSize="15px" color="#464C53">
            This will move {selectedStories.length > 1 ? "the selected interviews" : "the selected interview"} to
            another project.
          </Typography>
          <Typography fontSize="15px" color="#464C53">
            Choose the destination project for {selectedStories.length > 1 ? "these interviews" : "this interview"}.
          </Typography>
        </Box>

        <FormControl fullWidth size="small" disabled={isLoadingProjects || isSubmitting}>
          <InputLabel id="move-to-project-select-label">Select a project</InputLabel>
          <Select
            labelId="move-to-project-select-label"
            value={targetProjectId}
            label="Select a project"
            onChange={(event) => setTargetProjectId(event.target.value)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: "200px",
                },
              },
            }}
          >
            {availableProjects.map((project: Project) => (
              <MenuItem key={project._id} value={project._id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isLoadingProjects && availableProjects.length === 0 && (
          <Typography fontSize="14px" color="#7A8691">
            No other available projects were found for this action.
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-end" columnGap={1}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!targetProjectId || isSubmitting || isLoadingProjects}
            sx={{
              textTransform: "none",
              minWidth: "140px",
            }}
          >
            {isSubmitting ? <CircularProgress size="20px" color="inherit" /> : "Confirm move"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
